import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useStructuredData } from '@/hooks/useStructuredData';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import SocialProof from './components/SocialProof';
import PricingSection from './components/PricingSection';
import WhyChooseUs from './components/WhyChooseUs';
import CompatibleDevices from './components/CompatibleDevices';
import SatisfactionGuarantee from './components/SatisfactionGuarantee';
import WorldCoverage from './components/WorldCoverage';
import FreeTrial from './components/FreeTrial';
import FAQ from './components/FAQ';
import Support from './components/Support';
import CTASection from './components/CTASection';
import Footer from './components/Footer';

const HOME_SCHEMAS = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Onetvservices',
    url: 'https://onetvservices.com',
    logo: 'https://storage.readdy-site.link/project_files/ea087323-c332-4d1a-a768-642201e38806/8e03886a-a466-4209-8fc0-30625673693c_ONETV.png?v=410354e2b00bf17cf48639a5651635ec',
    foundingDate: '2019',
    description: 'Premium IPTV streaming service with +25,000 live channels and +70,000 VOD titles in HD and 4K quality. Serving 10,000+ subscribers in 150+ countries.',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+1-208-974-6525',
        contactType: 'customer support',
        availableLanguage: ['English', 'French', 'Arabic'],
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
          opens: '10:00',
          closes: '24:00',
        },
      },
    ],
    email: 'onetvservices@gmail.com',
    sameAs: ['https://wa.me/12089746525'],
    areaServed: 'Worldwide',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Onetvservices',
    url: 'https://onetvservices.com',
    description: 'Premium IPTV subscriptions — +25,000 channels, +70,000 VOD, HD & 4K quality. 7-day money-back guarantee. No auto-renewal.',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://onetvservices.com/?s={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'IPTV Streaming Subscription',
    provider: {
      '@type': 'Organization',
      name: 'Onetvservices',
      url: 'https://onetvservices.com',
    },
    serviceType: 'IPTV Streaming',
    description: 'Fixed-term IPTV subscriptions with access to +25,000 live TV channels and +70,000 movies & series in HD and 4K. Available in 1, 3, 6, and 12-month plans.',
    areaServed: 'Worldwide',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'IPTV Subscription Plans',
      itemListElement: [
        { '@type': 'Offer', name: '1 Month IPTV', priceCurrency: 'USD', price: '12.99' },
        { '@type': 'Offer', name: '3 Months IPTV', priceCurrency: 'USD', price: '24.99' },
        { '@type': 'Offer', name: '6 Months IPTV', priceCurrency: 'USD', price: '39.99' },
        { '@type': 'Offer', name: '12 Months IPTV', priceCurrency: 'USD', price: '59.99' },
      ],
    },
  },
];

export default function HomePage() {
  const { t } = useTranslation();
  const [showTop, setShowTop] = useState(false);
  useStructuredData(HOME_SCHEMAS);

  useEffect(() => {
    // Scroll-to-top visibility
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll);

    // Scroll reveal
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));

    return () => {
      window.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main>
        <HeroSection />
        <div className="reveal"><SocialProof /></div>
        <div className="reveal reveal-delay-1"><PricingSection /></div>
        <div className="reveal"><FreeTrial /></div>
        <div className="reveal"><WhyChooseUs /></div>
        <div className="reveal reveal-delay-1"><CompatibleDevices /></div>
        <div className="reveal"><SatisfactionGuarantee /></div>
        <div className="reveal reveal-delay-1"><WorldCoverage /></div>
        <div className="reveal"><FAQ /></div>
        <div className="reveal"><Support /></div>
        <div className="reveal"><CTASection /></div>
      </main>
      <Footer />
      {/* Scroll to top */}
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-24 right-6 z-50 w-10 h-10 rounded-full bg-gray-800/90 border border-white/10 text-white flex items-center justify-center hover:bg-gray-700 transition-all duration-300 hover:scale-110 cursor-pointer"
          aria-label="Scroll to top"
        >
          <i className="ri-arrow-up-line text-lg" />
        </button>
      )}

      {/* WhatsApp floating button */}
      <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3">
        <div className="group relative">
          {/* Pulse rings */}
          <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-30 animate-ping" />
          <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-20 animate-ping" style={{ animationDelay: '0.3s' }} />
          {/* Tooltip on hover */}
          <span className="
            absolute right-16 top-1/2 -translate-y-1/2
            bg-gray-900 text-white text-sm font-medium px-3 py-2 rounded-lg
            whitespace-nowrap pointer-events-none
            opacity-0 translate-x-2
            group-hover:opacity-100 group-hover:translate-x-0
            transition-all duration-300
            shadow-lg
          ">
            {t('whatsapp.tooltip')}
            <span className="absolute right-[-6px] top-1/2 -translate-y-1/2 border-4 border-transparent border-l-gray-900" />
          </span>
          <a
            id="btn_whatsapp"
            href="https://wa.me/12089746525"
            target="_blank"
            rel="noopener noreferrer"
            className="relative w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#20BA5A] text-white flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer shadow-lg"
            aria-label="Contact via WhatsApp"
          >
            <i className="ri-whatsapp-line text-3xl" />
          </a>
        </div>
      </div>
    </div>
  );
}
